import { useCallback } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import type {
  ConfigEntityDetail,
  EntityBulkOperationStatus,
} from '@web-config-app/core';
import { importEntitiesDB as db } from '@web-config-app/core';

// Bind singleton methods once for reuse across all hook instances
const getEntityEntryById = db.getEntityEntryById.bind(db);
const getEntityEntryByNewId = db.getEntityEntryByNewId.bind(db);

/**
 * A React hook that provides an interface for managing entities in IndexedDB
 * for import operations.
 *
 * This hook offers reactive queries and CRUD operations for entities stored in IndexedDB,
 * with optional filtering by domain entity type. It uses Dexie's live query functionality
 * to automatically update when the underlying data changes.
 *
 * @param params - Configuration parameters for the hook
 * @param params.domainEntityType - Optional domain entity type to filter results.
 *                                   When provided, only entities of this type will be queried and managed.
 *
 * @returns An object containing reactive data and methods for entity management:
 * - `entities`: Live-updating array of entity entries (or undefined while loading)
 * - `statusCounts`: Computed counts of entities by status (ready, complete, error, skipped)
 * - `clearEntitiesOfType`: Clears all entities of the specified domainEntityType (requires domainEntityType parameter)
 * - `getEntityEntryById`: Retrieves an entity entry by its ID
 * - `getEntityEntryByNewId`: Retrieves an entity entry by its newId field
 * - `addEntity`: Adds or updates a single entity with ready status
 * - `addEntities`: Bulk adds or updates multiple entities with ready status
 * - `updateEntityData`: Updates the entity data and newId fields for an entity
 * - `updateEntityStatus`: Updates the status field for an entity
 *
 * @example
 * ```tsx
 * // Get all entities
 * const { entities, statusCounts } = useImportEntitiesIndexedDB();
 *
 * // Filter by domain entity type
 * const {
 *   entities,
 *   clearEntitiesOfType,
 *   addEntity,
 *   updateEntityStatus
 * } = useImportEntitiesIndexedDB({ domainEntityType: 'config-activity' });
 *
 * // Add a new entity
 * await addEntity(myEntity);
 *
 * // Update entity status
 * await updateEntityStatus('entity-id', 'complete');
 *
 * // Check status counts
 * console.log(`Ready: ${statusCounts.ready}, Complete: ${statusCounts.complete}`);
 *
 * // Filter entities to import (using the utility from @web-config-app/core)
 * import { filterEntitiesToImport } from '@web-config-app/core';
 * const entitiesToImport = useMemo(() => filterEntitiesToImport(entities), [entities]);
 * ```
 */
export const useImportEntitiesIndexedDB = ({
  domainEntityType,
}: {
  domainEntityType?: string;
} = {}) => {
  const entities = useLiveQuery(
    () =>
      domainEntityType
        ? db.getEntitiesByDomainEntityType(domainEntityType)
        : db.entities.toArray(),
    [domainEntityType],
  );

  const statusCounts = {
    ready: entities?.filter((entity) => entity.status === 'ready').length ?? 0,
    complete:
      entities?.filter((entity) => entity.status === 'complete').length ?? 0,
    error: entities?.filter((entity) => entity.status === 'error').length ?? 0,
    skipped:
      entities?.filter((entity) => entity.status === 'skipped').length ?? 0,
  };

  const clearEntitiesOfType = useCallback(async () => {
    if (domainEntityType) {
      return db.transaction('rw', db.entities, async () =>
        db.clearAll(domainEntityType),
      );
    }

    // eslint-disable-next-line no-console
    console.error(
      'useImportEntitiesIndexedDB: clearEntitiesOfType called without a domainEntityType. Initialize the hook with a domainEntityType to use this method.',
    );
    return undefined;
  }, [domainEntityType]);

  const addEntity = useCallback(
    async (entityToAdd: ConfigEntityDetail, sourceTenantId?: string) =>
      db.transaction('rw', db.entities, async () =>
        db.upsertEntity({
          id: entityToAdd.id,
          data: entityToAdd,
          sourceTenantId,
          status: 'ready',
        }),
      ),
    [],
  );

  const addEntities = useCallback(
    async (entitiesToUpsert: ConfigEntityDetail[], sourceTenantId?: string) =>
      db.transaction('rw', db.entities, async () =>
        db.bulkUpsertEntities(entitiesToUpsert, sourceTenantId),
      ),
    [],
  );

  const updateEntityData = useCallback(
    async (id: string, newData: ConfigEntityDetail) =>
      db.transaction('rw', db.entities, async () =>
        db.updateEntityDataById(id, newData),
      ),
    [],
  );

  const updateEntityStatus = useCallback(
    async (id: string, status: EntityBulkOperationStatus) =>
      db.transaction('rw', db.entities, async () =>
        db.updateEntityStatusById(id, status),
      ),
    [],
  );

  return {
    entities,
    clearEntitiesOfType,
    statusCounts,
    getEntityEntryById,
    getEntityEntryByNewId,
    addEntity,
    addEntities,
    updateEntityData,
    updateEntityStatus,
  };
};
